import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { ASSISTANT_SCROLL_FOLLOW_THRESHOLD_PIXELS } from '@/constants/assistant';
import { flattenAssistantMessagePages, useAssistantMessagesQuery } from '@/hooks/useAssistant';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { useAssistantTurn } from '@/hooks/useAssistantTurn';
import { showErrorNotification } from '@/utils/notifications';
import { AssistantComposer } from './AssistantComposer';
import { AssistantEmptyState } from './AssistantEmptyState';
import { AssistantMessageList } from './AssistantMessageList';
import { useAssistantPanel } from './assistantPanelContext';
import { useReplyAnnouncements } from './useReplyAnnouncements';
import { resolveAssistantPageContext } from './uiActions/assistantDestinations';

interface AssistantChatViewProps {
  turn: ReturnType<typeof useAssistantTurn>;
}

function isNearBottom(element: HTMLElement): boolean {
  return (
    element.scrollHeight - element.scrollTop - element.clientHeight <=
    ASSISTANT_SCROLL_FOLLOW_THRESHOLD_PIXELS
  );
}

export function AssistantChatView({ turn }: AssistantChatViewProps) {
  const { t } = useTranslation('assistant');
  const location = useLocation();
  const {
    conversationId,
    aboutContext,
    pendingQuestion,
    consumePendingQuestion,
    pendingBriefKind,
    consumePendingBrief,
  } = useAssistantPanel();
  const messagesQuery = useAssistantMessagesQuery(conversationId);
  const pageContext = resolveAssistantPageContext(location.pathname, location.search);
  const messages = flattenAssistantMessagePages(messagesQuery.data);
  const { liveTurn, isBusy, send, startBrief, stop } = turn;
  const visibleLiveTurn =
    liveTurn !== null && liveTurn.conversationId === conversationId ? liveTurn : null;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isFollowingRef = useRef(true);
  useReplyAnnouncements(visibleLiveTurn);

  const ask = useCallback(
    function askQuestion(question: string): Promise<boolean> {
      isFollowingRef.current = true;
      return send(question, pageContext);
    },
    [send, pageContext]
  );

  const isShowingTurnOfAnotherConversation =
    liveTurn !== null && liveTurn.conversationId !== conversationId;

  useEffect(
    function askPendingQuestion() {
      if (pendingQuestion === null || isBusy || isShowingTurnOfAnotherConversation) {
        return;
      }
      const question = consumePendingQuestion();
      if (question !== null) {
        void ask(question);
      }
    },
    [pendingQuestion, isBusy, isShowingTurnOfAnotherConversation, consumePendingQuestion, ask]
  );

  useEffect(
    function startPendingBrief() {
      if (pendingBriefKind === null || isBusy) {
        return;
      }
      const kind = consumePendingBrief();
      if (kind === null) {
        return;
      }
      isFollowingRef.current = true;
      startBrief(kind).catch(function reportBriefFailure(briefError: unknown) {
        const message = resolveApiErrorMessage(briefError);
        showErrorNotification(message.length > 0 ? message : t('brief.failed'));
      });
    },
    [pendingBriefKind, isBusy, consumePendingBrief, startBrief, t]
  );

  const liveText = visibleLiveTurn === null ? '' : visibleLiveTurn.text;
  const liveActivityCount = visibleLiveTurn === null ? 0 : visibleLiveTurn.activities.length;
  useEffect(
    function followNewestMessage() {
      const container = scrollContainerRef.current;
      if (container !== null && isFollowingRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    },
    [messages.length, liveText, liveActivityCount]
  );

  const contextLabel =
    aboutContext !== null
      ? t('composer.about', { subject: aboutContext })
      : pageContext.routeKey === null
        ? null
        : t('destinations.' + pageContext.routeKey, { defaultValue: '' }) || null;
  const isLoadingConversation = conversationId !== null && messagesQuery.isLoading;
  const hasConversationError =
    conversationId !== null && messagesQuery.isError && messagesQuery.data === undefined;
  const isEmpty = messages.length === 0 && visibleLiveTurn === null;

  return (
    <>
      <div
        ref={scrollContainerRef}
        onScroll={function rememberScrollPosition(event) {
          isFollowingRef.current = isNearBottom(event.currentTarget);
        }}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col"
      >
        {isLoadingConversation ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        ) : hasConversationError ? (
          <QueryErrorState
            onRetry={function retry() {
              void messagesQuery.refetch();
            }}
          />
        ) : isEmpty ? (
          <AssistantEmptyState
            routeKey={pageContext.routeKey}
            onAsk={function askSuggestion(question) {
              void ask(question);
            }}
          />
        ) : (
          <>
            {messagesQuery.hasNextPage ? (
              <div className="pt-3 flex justify-center">
                <Button
                  variant="ghost"
                  size="chip"
                  isLoading={messagesQuery.isFetchingNextPage}
                  onClick={function loadOlderMessages() {
                    isFollowingRef.current = false;
                    void messagesQuery.fetchNextPage();
                  }}
                >
                  {t('panel.loadOlder')}
                </Button>
              </div>
            ) : null}
            <AssistantMessageList messages={messages} liveTurn={visibleLiveTurn} />
          </>
        )}
      </div>
      <AssistantComposer isBusy={isBusy} contextLabel={contextLabel} onSend={ask} onStop={stop} />
    </>
  );
}
